import React, { useState, useRef, useEffect } from "react";
import { Send, ArrowLeft, MoreVertical } from "lucide-react";
import Button from "../../../../components/ui/Button";
import TextArea from "../../../../components/ui/TextArea";

const ChatWindow = ({ conversation, onSendMessage, onBack }) => {
  const [newMessage, setNewMessage] = useState("");
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [conversation.messages]);

  const handleSend = () => {
    if (newMessage.trim()) {
      onSendMessage(newMessage);
      setNewMessage("");
    }
  };

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-200">
        <div className="flex items-center">
          {onBack && (
            <button onClick={onBack} className="mr-3 @md:hidden text-slate-500 hover:text-slate-700">
              <ArrowLeft size={20} />
            </button>
          )}
          <img
            src={conversation.creatorProfilePictureUrl}
            alt={conversation.creatorName}
            className="w-10 h-10 rounded-full object-cover mr-3"
          />
          <div>
            <h3 className="font-semibold text-slate-800">{conversation.creatorName}</h3>
            <span className="text-xs text-green-500 font-medium">Online</span>
          </div>
        </div>
        <Button variant="ghost" size="icon">
          <MoreVertical size={20} className="text-slate-500" />
        </Button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
        {conversation.messages.map((msg) => {
          const isMe = msg.sender === "creator";
          return (
            <div key={msg.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[75%] rounded-2xl px-4 py-3 text-sm ${
                  isMe
                    ? "bg-primary text-white rounded-tr-none"
                    : "bg-white text-slate-800 border border-slate-200 rounded-tl-none shadow-sm"
                }`}
              >
                <p>{msg.text}</p>
                <p className={`text-[10px] mt-1 text-right ${isMe ? "text-purple-200" : "text-slate-400"}`}>
                  {msg.timestamp}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Quick Replies */}
      <div className="px-4 pt-2 pb-0 bg-white border-t border-slate-200">
        <p className="text-xs font-medium text-slate-500 mb-2 flex items-center gap-1">
            <span role="img" aria-label="sparkles">✨</span> Quick Replies
        </p>
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {[
            "Thanks for the support! ❤️",
            "Working on new music! 🎵",
            "Stay tuned! 👀",
            "Can't wait to see you there! 🎉"
          ].map((reply) => (
            <button
              key={reply}
              onClick={() => setNewMessage(reply)}
              className="whitespace-nowrap px-3 py-1 bg-purple-50 hover:bg-purple-100 text-purple-700 text-xs rounded-full border border-purple-100 transition-colors"
            >
              {reply}
            </button>
          ))}
        </div>
      </div>

      {/* Input */}
      <div className="p-4 bg-white pt-2">
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <TextArea
              placeholder="Type a message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              className="min-h-[44px] max-h-32 resize-none py-3"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
            />
          </div>
          <Button
            variant="primary"
            onClick={handleSend}
            className="h-[44px] w-[44px] rounded-full flex items-center justify-center p-0 shrink-0"
            disabled={!newMessage.trim()}
          >
            <Send size={18} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChatWindow;
